import { styled } from '@mui/material/styles';
import type { ComponentPropsWithoutRef, CSSProperties } from 'react';
import { forwardRef } from 'react';
import { intent, radius, sizes, spacingTokens, typography } from '../../theme/tokens';

/**
 * FilterBadge — the count badge shown inside a filter trigger when several values are applied.
 *
 * A square (16×16) primary-solid counter that grows horizontally for multi-digit counts
 * (sp2 side padding, min-width h16), with `captionSm` text on `intent.primary.onSolid`.
 *
 * NOT MUI's `Badge`: that positions absolutely to overlay the top-right of an icon/avatar.
 * This one sits inline in the button's label flow, after the text (sp6 gap). Local to the
 * Filter family — see Filter.usage.md.
 *
 * A visually-hidden " selected" suffix gives the bare number context for screen readers
 * (the trigger otherwise announces as e.g. "Format 3").
 *
 * **Render it only when there is a count to show.** It used to carry a `reserved` mode that held
 * the badge's footprint invisibly at count 0; that was dropped on 2026-08-26 when the family
 * gained the value-or-count display rule — a trigger can now grow by a *named value*, always
 * wider than a badge, so reserving the badge's box no longer prevented any reflow.
 */
const StyledFilterBadgeRoot = styled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxSizing: 'border-box',
  minWidth: sizes.height.h16,
  height: sizes.height.h16,
  marginLeft: spacingTokens.sp6,
  paddingLeft: spacingTokens.sp2,
  paddingRight: spacingTokens.sp2,
  borderRadius: radius.sm,
  backgroundColor: intent.primary.solid,
  color: intent.primary.onSolid,
  // captionSm — 10px/14px regular, per Figma 1458:394 (designer, 2026-08-27). Was captionBold
  // (12px/16px semibold); the count now reads quieter inside its 16px box.
  ...typography.captionSm,
});

const srOnlyStyle: CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
};

export type FilterBadgeProps = ComponentPropsWithoutRef<'span'>;

export const FilterBadge = forwardRef<HTMLSpanElement, FilterBadgeProps>(function FilterBadge(
  { children, ...rest },
  ref
) {
  return (
    // `data-slot` is the stable hook the theme uses to key trailing padding on "is a badge
    // present" (MoreFiltersButton's right inset differs with and without one). Emotion's
    // generated class name is not stable enough to select on. Set before `...rest` so a caller
    // can still override it.
    <StyledFilterBadgeRoot ref={ref} data-slot="badge" {...rest}>
      {children}
      <span style={srOnlyStyle}>&nbsp;selected</span>
    </StyledFilterBadgeRoot>
  );
});
